import { useCallback, useEffect, useRef, useState } from "react";

import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";

import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import { getAuthStorageNamespace } from "../auth/auth";

import {
  CloudConflictError,
  deleteCloudDrawing,
  getCloudDrawing,
  listCloudDrawings,
  saveCloudDrawing,
  subscribeToCloudUpdates,
} from "../data/cloudStorage";

import type {
  CloudDrawing,
  CloudSubscription,
  CloudUpdateEvent,
} from "../data/cloudStorage";

const CLOUD_DRAWING_ID_STORAGE_KEY = "excalidraw-cloud-drawing-id";
const CLOUD_DRAWING_NAME_STORAGE_KEY = "excalidraw-cloud-drawing-name";
const CLOUD_DRAWING_UPDATED_AT_STORAGE_KEY =
  "excalidraw-cloud-drawing-updated-at";
const CLOUD_AUTOSAVE_DELAY = 2000;

const getCloudStorageKey = (key: string) => {
  return `${key}:${getAuthStorageNamespace()}`;
};

const generateClientId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `client-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export type CloudOpenDialogState = {
  drawings: CloudDrawing[];
  error: string | null;
  isLoading: boolean;
  isOpen: boolean;
  openingDrawingId: string | null;
};

export type UseCloudStorageOptions = {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  onError: (message: string) => void;
};

export type UseCloudStorageReturn = {
  drawingId: string | null;
  drawingName: string | null;
  dialogState: CloudOpenDialogState;
  scheduleAutosave: (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => void;
  saveCurrent: (opts?: { saveAs?: boolean }) => Promise<void>;
  deleteCurrent: () => Promise<void>;
  openDialog: () => void;
  closeDialog: () => void;
  openSelected: (drawing: CloudDrawing) => Promise<void>;
  refreshList: () => Promise<void>;
};

export const useCloudStorage = ({
  excalidrawAPI,
  onError,
}: UseCloudStorageOptions): UseCloudStorageReturn => {
  const [drawingId, setDrawingId] = useState<string | null>(() => {
    return window.localStorage.getItem(
      getCloudStorageKey(CLOUD_DRAWING_ID_STORAGE_KEY),
    );
  });
  const [drawingName, setDrawingName] = useState<string | null>(() => {
    return window.localStorage.getItem(
      getCloudStorageKey(CLOUD_DRAWING_NAME_STORAGE_KEY),
    );
  });
  const drawingIdRef = useRef(drawingId);
  const drawingNameRef = useRef(drawingName);
  // Last server-confirmed updatedAt for the active drawing. Drives:
  //   1. SSE event filtering (skip pull if event timestamp matches)
  //   2. Optimistic concurrency check on autosave PUTs (`expectedUpdatedAt`)
  // Persisted to localStorage so a freshly loaded tab knows whether its cached
  // scene matches what the server currently holds.
  const lastKnownUpdatedAtRef = useRef<string | null>(
    window.localStorage.getItem(
      getCloudStorageKey(CLOUD_DRAWING_UPDATED_AT_STORAGE_KEY),
    ),
  );
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Autosave stays off on page load even when a drawingId is cached in
  // localStorage: the cached scene may be stale (another device, or a CLI
  // edit) and we don't want to silently overwrite the server with it. It
  // flips on inside `remember` (called from open/save flows).
  const autosaveEnabledRef = useRef(false);
  // True while we're applying a remote-pulled scene to the canvas. The
  // resulting onChange would otherwise trigger an autosave that re-uploads
  // the identical content we just received.
  const applyingRemoteRef = useRef(false);
  const latestSceneRef = useRef<{
    elements: readonly OrderedExcalidrawElement[];
    appState: AppState;
    files: BinaryFiles;
  } | null>(null);
  // Stable per-tab identity, used to tag PUT/DELETE so the server can echo it
  // back in the SSE broadcast. Subscribers ignore events that originated in
  // their own tab.
  const clientIdRef = useRef<string>(generateClientId());
  const subscriptionRef = useRef<CloudSubscription | null>(null);
  const [dialogState, setDialogState] = useState<CloudOpenDialogState>({
    drawings: [],
    error: null,
    isLoading: false,
    isOpen: false,
    openingDrawingId: null,
  });

  useEffect(() => {
    drawingIdRef.current = drawingId;
    drawingNameRef.current = drawingName;
  }, [drawingId, drawingName]);

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  const persistLastKnownUpdatedAt = useCallback((value: string | null) => {
    lastKnownUpdatedAtRef.current = value;
    if (value) {
      window.localStorage.setItem(
        getCloudStorageKey(CLOUD_DRAWING_UPDATED_AT_STORAGE_KEY),
        value,
      );
    } else {
      window.localStorage.removeItem(
        getCloudStorageKey(CLOUD_DRAWING_UPDATED_AT_STORAGE_KEY),
      );
    }
  }, []);

  const remember = useCallback(
    (drawing: CloudDrawing) => {
      setDrawingId(drawing.id);
      setDrawingName(drawing.name);
      drawingIdRef.current = drawing.id;
      drawingNameRef.current = drawing.name;
      autosaveEnabledRef.current = true;
      persistLastKnownUpdatedAt(drawing.updatedAt);
      window.localStorage.setItem(
        getCloudStorageKey(CLOUD_DRAWING_ID_STORAGE_KEY),
        drawing.id,
      );
      window.localStorage.setItem(
        getCloudStorageKey(CLOUD_DRAWING_NAME_STORAGE_KEY),
        drawing.name,
      );
    },
    [persistLastKnownUpdatedAt],
  );

  const forget = useCallback(() => {
    setDrawingId(null);
    setDrawingName(null);
    drawingIdRef.current = null;
    drawingNameRef.current = null;
    autosaveEnabledRef.current = false;
    persistLastKnownUpdatedAt(null);
    window.localStorage.removeItem(
      getCloudStorageKey(CLOUD_DRAWING_ID_STORAGE_KEY),
    );
    window.localStorage.removeItem(
      getCloudStorageKey(CLOUD_DRAWING_NAME_STORAGE_KEY),
    );
  }, [persistLastKnownUpdatedAt]);

  const getDefaultName = useCallback(() => {
    return (
      drawingNameRef.current ||
      excalidrawAPI
        ?.getName()
        ?.replace(/\.excalidraw$/i, "")
        .trim() ||
      "Untitled drawing"
    );
  }, [excalidrawAPI]);

  const serializeScene = useCallback(
    (
      elements?: readonly OrderedExcalidrawElement[],
      appState?: AppState,
      files?: BinaryFiles,
    ) => {
      if (!excalidrawAPI && (!elements || !appState || !files)) {
        throw new Error("Excalidraw is not ready yet.");
      }
      return serializeAsJSON(
        elements || excalidrawAPI!.getSceneElementsIncludingDeleted(),
        appState || excalidrawAPI!.getAppState(),
        files || excalidrawAPI!.getFiles(),
        "local",
      );
    },
    [excalidrawAPI],
  );

  // Apply a payload pulled from the server onto the canvas. Used by both the
  // explicit "open" flow and the live-update path. Sets applyingRemoteRef so
  // the resulting onChange does not bounce back to the server.
  const applyCloudPayload = useCallback(
    async (
      drawing: CloudDrawing,
      payload: string,
      opts?: { recenter?: boolean },
    ) => {
      if (!excalidrawAPI) {
        return;
      }
      applyingRemoteRef.current = true;
      try {
        // Viewport (scroll/zoom) is per-tab, never per-cloud. Without this
        // capture, two tabs panning the same drawing ping-pong each other:
        // tab A pans, autosaves, broadcasts; tab B pulls and gets A's scroll;
        // B then autosaves its (now A-derived) viewport back, which broadcasts
        // to A with B's clientId — A pulls and inherits the round-tripped
        // scroll. Keeping local viewport on every live-update apply means
        // each tab stays put.
        const localView = {
          scrollX: excalidrawAPI.getAppState().scrollX,
          scrollY: excalidrawAPI.getAppState().scrollY,
          zoom: excalidrawAPI.getAppState().zoom,
        };
        const data = await loadFromBlob(
          new Blob([payload], { type: "application/json" }),
          excalidrawAPI.getAppState(),
          null,
        );
        const loadedFiles = Object.values(data.files || {});
        if (loadedFiles.length) {
          excalidrawAPI.addFiles(loadedFiles);
        }
        excalidrawAPI.updateScene({
          elements: data.elements,
          appState: {
            ...data.appState,
            // Live-update path: keep our existing viewport. The open-from-cloud
            // path recenters via setViewport below, so it doesn't need
            // this override.
            ...(opts?.recenter ? {} : localView),
            fileHandle: null,
            isLoading: false,
          },
          captureUpdate: CaptureUpdateAction.NEVER,
        });
        excalidrawAPI.history.clear();
        if (opts?.recenter && data.elements.length > 0) {
          excalidrawAPI.setViewport({
            target: data.elements,
            fit: "scale-down",
            animation: false,
          });
        }
        remember(drawing);
      } finally {
        // Let the synchronous onChange fire under the suppress flag, then
        // clear it so future user edits resume normal autosave.
        setTimeout(() => {
          applyingRemoteRef.current = false;
        }, 0);
      }
    },
    [excalidrawAPI, remember],
  );

  const saveCurrent = useCallback(
    async ({ saveAs = false }: { saveAs?: boolean } = {}) => {
      if (!excalidrawAPI) {
        return;
      }
      const name = window.prompt("Название файла на сервере", getDefaultName());
      if (name === null) {
        return;
      }
      try {
        const drawing = await saveCloudDrawing({
          id: saveAs ? null : drawingIdRef.current,
          name,
          payload: serializeScene(),
          clientId: clientIdRef.current,
        });
        remember(drawing);
        excalidrawAPI.setToast({
          message: `Сохранено на сервере: ${drawing.name}`,
          duration: 3000,
        });
      } catch (error: any) {
        onError(error.message);
      }
    },
    [excalidrawAPI, getDefaultName, remember, serializeScene, onError],
  );

  const scheduleAutosave = useCallback(
    (
      elements: readonly OrderedExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
    ) => {
      if (
        applyingRemoteRef.current ||
        !autosaveEnabledRef.current ||
        !drawingIdRef.current
      ) {
        return;
      }
      latestSceneRef.current = { elements, appState, files };
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
      autosaveTimerRef.current = setTimeout(async () => {
        const id = drawingIdRef.current;
        const scene = latestSceneRef.current;
        if (!id || !scene) {
          return;
        }
        try {
          const drawing = await saveCloudDrawing({
            id,
            name: getDefaultName(),
            payload: serializeScene(
              scene.elements,
              scene.appState,
              scene.files,
            ),
            expectedUpdatedAt: lastKnownUpdatedAtRef.current,
            clientId: clientIdRef.current,
          });
          remember(drawing);
        } catch (error: any) {
          if (error instanceof CloudConflictError) {
            // Another client wrote the drawing first. Don't overwrite; the
            // SSE channel will deliver the newer version momentarily and we
            // will pull it. The next user edit triggers a fresh autosave.
            console.warn("Cloud autosave conflict, awaiting live update.");
            return;
          }
          if (String(error.message || "").includes("not found")) {
            forget();
            excalidrawAPI?.setToast({
              message: "Серверный файл не найден. Сохраните доску заново.",
              duration: 4000,
            });
          } else {
            console.error(error);
          }
        }
      }, CLOUD_AUTOSAVE_DELAY);
    },
    [excalidrawAPI, forget, getDefaultName, remember, serializeScene],
  );

  const refreshList = useCallback(async () => {
    setDialogState((state) => ({ ...state, error: null, isLoading: true }));
    try {
      const drawings = await listCloudDrawings();
      setDialogState((state) => ({
        ...state,
        drawings,
        error: null,
        isLoading: false,
      }));
    } catch (error: any) {
      setDialogState((state) => ({
        ...state,
        error: error.message,
        isLoading: false,
      }));
    }
  }, []);

  const openDialog = useCallback(() => {
    if (!excalidrawAPI) {
      return;
    }
    setDialogState((state) => ({ ...state, error: null, isOpen: true }));
    void refreshList();
  }, [excalidrawAPI, refreshList]);

  const closeDialog = useCallback(() => {
    setDialogState((state) => ({
      ...state,
      isOpen: false,
      openingDrawingId: null,
    }));
  }, []);

  const openSelected = useCallback(
    async (selected: CloudDrawing) => {
      if (!excalidrawAPI) {
        return;
      }
      setDialogState((state) => ({
        ...state,
        error: null,
        openingDrawingId: selected.id,
      }));
      try {
        const { drawing, payload } = await getCloudDrawing(selected.id);
        await applyCloudPayload(drawing, payload, { recenter: true });
        setDialogState((state) => ({
          ...state,
          isOpen: false,
          openingDrawingId: null,
        }));
        excalidrawAPI.setToast({
          message: `Открыто с сервера: ${drawing.name}`,
          duration: 3000,
        });
      } catch (error: any) {
        setDialogState((state) => ({
          ...state,
          error: error.message,
          openingDrawingId: null,
        }));
      }
    },
    [excalidrawAPI, applyCloudPayload],
  );

  const deleteCurrent = useCallback(async () => {
    if (!excalidrawAPI || !drawingIdRef.current) {
      return;
    }
    const name = getDefaultName();
    if (!window.confirm(`Удалить серверный файл "${name}"?`)) {
      return;
    }
    try {
      await deleteCloudDrawing(drawingIdRef.current, clientIdRef.current);
      forget();
      excalidrawAPI.setToast({
        message: "Серверный файл удален",
        duration: 3000,
      });
    } catch (error: any) {
      onError(error.message);
    }
  }, [excalidrawAPI, forget, getDefaultName, onError]);

  // SSE subscription. Reconnects automatically. One subscription per drawing;
  // when drawingId changes (open another, or forget), close the old one.
  useEffect(() => {
    if (!drawingId || !excalidrawAPI) {
      return undefined;
    }

    const handleEvent = async (event: CloudUpdateEvent) => {
      if (event.type === "deleted") {
        // Another client deleted the drawing. Don't trigger our own DELETE.
        if (event.originClientId === clientIdRef.current) {
          return;
        }
        forget();
        excalidrawAPI.setToast({
          message: "Серверный файл был удалён",
          duration: 4000,
        });
        return;
      }

      // "update" events carry an originClientId; the "ready" snapshot does not.
      // Skip "update" echoes from our own PUTs.
      if (
        event.type === "update" &&
        event.originClientId === clientIdRef.current
      ) {
        return;
      }
      if (event.updatedAt === lastKnownUpdatedAtRef.current) {
        return;
      }
      try {
        const { drawing, payload } = await getCloudDrawing(event.id);
        await applyCloudPayload(drawing, payload);
        excalidrawAPI.setToast({
          message: "Обновлено с сервера",
          duration: 2000,
        });
      } catch (error: any) {
        console.warn("Failed to apply live update:", error.message || error);
      }
    };

    const subscription = subscribeToCloudUpdates(
      drawingId,
      handleEvent,
      (error) => {
        console.warn("Cloud update stream error:", error);
      },
    );
    subscriptionRef.current = subscription;

    return () => {
      subscription.close();
      if (subscriptionRef.current === subscription) {
        subscriptionRef.current = null;
      }
    };
  }, [drawingId, excalidrawAPI, applyCloudPayload, forget]);

  return {
    drawingId,
    drawingName,
    dialogState,
    scheduleAutosave,
    saveCurrent,
    deleteCurrent,
    openDialog,
    closeDialog,
    openSelected,
    refreshList,
  };
};
